import React, { lazy } from 'react';

import { Navigate, type RouteObject, useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@pkg/hooks';
import { PrismTypes } from '@pkg/types';

import { AuthProviders } from '@app/client/resources/w3auth/types';
import config from '@app/config';
import { PRIMARY_YAT_SELECTION, TWITTER_CONNECT_CONFIRM, TWITTER_CONNECT_ERROR } from '@app/pages/GetStarted/routes';
import { useSentryRoutes } from '@app/routes/hooks/useSentryRoutes';
import { EmojiIDRoutes, FantasyRoutes, PartnerRoutes } from '@app/routes/sections';
import { PathParams } from '@app/routes/types';

// Sections
const AdminRoutes = lazy(() => import('./sections/AdminRoutes'));
const AdminFantasyRoutes = lazy(() => import('./sections/AdminFantasyRoutes'));
const ArtifactRoutes = lazy(() => import('./sections/ArtifactRoutes'));

const FeatureFlagRoutes = lazy(() =>
    import('@app/routes/components/FeatureFlagRoutes').then((mod) => ({
        default: mod.FeatureFlagRoutes,
    })),
);
const NavigateHelperComponent = lazy(() =>
    import('@app/routes/components/NavigationHelper').then((mod) => ({
        default: mod.NavigateHelperComponent,
    })),
);
const ProtectedRoute = lazy(() =>
    import('@app/routes/components/ProtectedRoute').then((mod) => ({
        default: mod.ProtectedRoute,
    })),
);
const PublicRoute = lazy(() =>
    import('@app/routes/components/PublicRoute').then((mod) => ({
        default: mod.PublicRoute,
    })),
);
const RedirectWrapper = lazy(() =>
    import('@app/routes/components/RedirectWrapper').then((mod) => ({
        default: mod.RedirectWrapper,
    })),
);
/////////////////

const TransferConfirmationScreen = lazy(() =>
    import('@pkg/components/Transfers/TransferConfirmationScreen').then((mod) => ({
        default: mod.TransferConfirmationScreen,
    })),
);

// TODO: REMOVE THIS TEMPORARY ROUTE FOR W3A
const W3aStandAloneFlow = lazy(() =>
    import('@app/pages/w3a/W3aStandAloneFlow').then((mod) => ({
        default: mod.W3aStandAloneFlow,
    })),
);
const W3aCallback = lazy(() =>
    import('@app/pages/w3a/W3aCallback').then((mod) => ({
        default: mod.W3aCallback,
    })),
);

const ConfirmTwitterAccount = lazy(() =>
    import('@app/pages/GetStarted/TwitterConnect').then((mod) => ({ default: mod.ConfirmTwitterAccount })),
);
const ErrorTwitterConnect = lazy(() =>
    import('@app/pages/GetStarted/TwitterConnect').then((mod) => ({ default: mod.ErrorTwitterConnect })),
);

const ForgotPassword = lazy(() =>
    import('@app/pages/v2/UserAccount/ForgotPassword').then((mod) => ({ default: mod.ForgotPassword })),
);
const LinkExpired = lazy(() =>
    import('@app/pages/v2/UserAccount/LinkExpired').then((mod) => ({ default: mod.LinkExpired })),
);

const MagicLinkSuccess = lazy(() =>
    import('@app/pages/v2/UserAccount/MagicLinkSuccess').then((mod) => ({ default: mod.MagicLinkSuccess })),
);

const PageNotFound = lazy(() => import('@app/pages/PageNotFound').then((mod) => ({ default: mod.PageNotFound })));
const Picker = lazy(() => import('@app/pages/Picker').then((mod) => ({ default: mod.Picker })));
const ResendMagicLink = lazy(() =>
    import('@app/pages/v2/UserAccount/ResendMagicLink').then((mod) => ({ default: mod.ResendMagicLink })),
);

const ArtifactMintPanel = lazy(() => import('@app/pages/Artifacts/ArtifactsExternalRedirect'));
const Checkout = lazy(() => import('@app/pages/Checkout'));
const CheckoutSuccess = lazy(() => import('@app/pages/CheckoutSuccess'));
const CollectionPage = lazy(() => import('@app/pages/Collection').then((mod) => ({ default: mod.Collection })));
const Dashboard = lazy(() => import('@app/pages/Dashboard').then((mod) => ({ default: mod.Dashboard })));

const GlobalSearch = lazy(() => import('@app/pages/GlobalSearch'));

const NftRenamePanel = lazy(() => import('@app/pages/NFT/NftRenamePanel'));
const ProfileEdit = lazy(() => import('@app/pages/Profile/ProfileEdit').then((mod) => ({ default: mod.ProfileEdit })));
const PrimaryYatSelection = lazy(() => import('@app/pages/GetStarted/PrimaryYatSelection'));
const Login = lazy(() => import('@app/pages/v2/UserAccount/Login'));
const NftDetailView = lazy(() => import('@app/pages/NFTDetailView').then((mod) => ({ default: mod.NFTDetailView })));
const WaitListEmailConfirmation = lazy(() => import('@app/pages/Waitlist/Confirmation'));
const WaitListEmail = lazy(() => import('@app/pages/Waitlist/Email'));
const WaitListLogin = lazy(() => import('@app/pages/Waitlist/Login'));

const WishlistsPage = lazy(() => import('@app/pages/Wishlist').then((mod) => ({ default: mod.WishlistsPage })));
const WishlistSinglePage = lazy(() =>
    import('@app/pages/Wishlist').then((mod) => ({ default: mod.WishlistSinglePage })),
);

const OpenPrismCase = lazy(() => import('@app/pages/LootBoxes/OpenPrismCase'));
const ResetPassword = lazy(() => import('@app/pages/v2/UserAccount/ResetPassword'));

const SecuritySettings = lazy(() => import('@app/pages/SecuritySettings'));
const SetPassword = lazy(() => import('@app/pages/v2/UserAccount/SetPassword'));
const Unauthorized = lazy(() => import('@pkg/components/Elements/Unauthorized/Unauthorized'));

const WalletRoute = lazy(() => import('@app/pages/Wallet').then((mod) => ({ default: mod.Wallet })));
const WalletManagerRoutes = lazy(() => import('@app/pages/WalletManager/Routes'));

const DiscoverPage = lazy(() => import('@app/pages/DiscoverPage'));
const StartPage = lazy(() => import('@app/pages/StartPage'));

const { termsLink, privacyLink } = config;

const wishlistTabPaths = ['following', 'popular', 'my-wishlists'];

export function useRoutesConfig() {
    const [params] = useSearchParams();
    const { isWalletManagerEnabled, yatFantasyEnabled, showDiscoverPage, showLegacyYativerse } = useAppSelector(
        (state) => state.featureFlags.features,
    );
    const withParams = params ? `?${params.toString()}` : '';
    const pathWithParams = (path: string) => `${path}${withParams}`;

    const tabRoutes = (tabPaths: string[], element: React.ReactElement): RouteObject[] =>
        tabPaths.map((path) => ({
            path,
            element,
        }));

    const routesConfig: RouteObject[] = [
        // Protected routes
        {
            path: '/',
            element: <ProtectedRoute />,
            children: [
                // index routes
                { index: true, element: <Navigate to={pathWithParams('/profile')} /> },
                { path: 'home', element: <Navigate to={pathWithParams('/profile')} /> },

                // PRIMARY YAT AND TWITTER
                { path: PRIMARY_YAT_SELECTION, element: <PrimaryYatSelection /> },
                // { path: TWITTER_CONNECT, element: <ConnectTwitterAccount /> },
                { path: TWITTER_CONNECT_CONFIRM, element: <ConfirmTwitterAccount /> },
                { path: TWITTER_CONNECT_ERROR, element: <ErrorTwitterConnect /> },

                {
                    path: 'yativerse',
                    element: showLegacyYativerse ? <Dashboard /> : <Navigate to={pathWithParams('/')} />,
                },
                // Rest of routes
                { path: 'admin/fantasy/*', element: <AdminFantasyRoutes /> },
                { path: 'admin/*', element: <AdminRoutes /> },
                { path: `checkout/:${PathParams.EidOrCartId}?`, element: <Checkout /> },

                { path: `:${PathParams.Yat}/success`, element: <CheckoutSuccess /> },
                // This is staying here until the discord bot updates the endpoint it sends users to
                { path: 'emojis/buy/*', element: <Checkout /> },
                { path: `:${PathParams.Yat}/success_crypto/:${PathParams.CryptoType}`, element: <CheckoutSuccess /> },

                {
                    path: `mint-artifact/:${PathParams.ArtifactType}/:${PathParams.Id}`,
                    element: <ArtifactMintPanel />,
                },
                { path: 'profile', element: <ProfileEdit /> },
                { path: 'profile/edit', element: <Navigate to={pathWithParams('/profile')} /> },
                { path: `rename/:${PathParams.Yat}`, element: <NftRenamePanel /> },
                { path: 'security-settings', element: <SecuritySettings /> },
                { path: 'set-password', element: <SetPassword /> },
                { path: 'change-password', element: <SetPassword /> },
                {
                    // TODO: Ask Biz about a 'log in with other account' option here too.
                    path: `transfer/:${PathParams.Yat}`,
                    element: <TransferConfirmationScreen />,
                },
                { path: 'unauthorized', element: <Unauthorized /> },
                {
                    path: 'wallet/*',
                    element: <FeatureFlagRoutes element={<WalletRoute />} isEnabled={isWalletManagerEnabled} />,
                },
                {
                    path: 'paywithyat/*',
                    element: <FeatureFlagRoutes element={<WalletManagerRoutes />} isEnabled={isWalletManagerEnabled} />,
                },
                {
                    path: 'wishlists',
                    element: <WishlistsPage />,
                    children: tabRoutes(wishlistTabPaths, <WishlistsPage />),
                },
            ],
        },
        // Public routes
        {
            path: '/',
            element: <PublicRoute />,
            children: [
                { path: 'start', element: <StartPage /> },
                { path: 'admin/sign-in', element: <Login /> },
                { path: 'waitlist-login', element: <WaitListLogin /> },
                { path: 'new-user-confirmation', element: <WaitListEmailConfirmation /> },
                { path: `forgot-password/:${PathParams.Email}?`, element: <ForgotPassword /> },
                { path: 'link-expired', element: <LinkExpired /> },
                { path: 'magic-link-success', element: <MagicLinkSuccess /> },
                { path: 'password-reset', element: <ResetPassword /> },
                { path: 'resend-link', element: <ResendMagicLink /> },
                { path: 'sign-in', element: <Navigate to="/auth/login" /> },
                {
                    path: 'auth/*',
                    children: [
                        { path: `sign-in-with-apple`, element: <W3aCallback provider={AuthProviders.Apple} /> },
                        { path: `sign-in-with-discord`, element: <W3aCallback provider={AuthProviders.Discord} /> },
                        { path: `sign-in-with-google`, element: <W3aCallback provider={AuthProviders.Google} /> },
                        { path: `sign-in-with-twitter`, element: <W3aCallback provider={AuthProviders.Twitter} /> },
                        { path: '*', element: <W3aStandAloneFlow /> },
                    ],
                },
            ],
        },
        {
            path: '/',
            element: <RedirectWrapper />,
            children: [
                {
                    path: 'discover',
                    element: showDiscoverPage ? <DiscoverPage /> : <Navigate to={pathWithParams('/')} />,
                },
                // Routes that can be both private or public.
                { path: `:${PathParams.Yat}/*`, element: <EmojiIDRoutes /> },
                { path: 'artifacts/*', element: <ArtifactRoutes /> },
                {
                    path: `collection/:${PathParams.Chain}/:${PathParams.CollectionSlug}`,
                    element: <CollectionPage />,
                },
                {
                    path: `collectible/:${PathParams.Chain}/:${PathParams.Contract}/:${PathParams.Id}`,
                    element: <NftDetailView />,
                },
                { path: 'create', element: <Picker /> },
                { path: 'create-new-user', element: <WaitListEmail /> },
                {
                    path: 'fantasy/*',
                    element: <FeatureFlagRoutes element={<FantasyRoutes />} isEnabled={yatFantasyEnabled} />,
                },
                { path: 'fantasy-games', element: <Navigate to={pathWithParams('/')} /> },
                { path: 'search', element: <GlobalSearch /> },
                {
                    path: `wishlists/:${PathParams.WishlistSlug}`,
                    element: <WishlistSinglePage />,
                },
                { path: `partner/:${PathParams.PartnerKey}/*`, element: <PartnerRoutes /> },
                { path: `prism-cases/open/:${PathParams.Id}`, element: <OpenPrismCase prismType={PrismTypes.case} /> },
                { path: `prism-keys/open/:${PathParams.Id}`, element: <OpenPrismCase prismType={PrismTypes.key} /> },
                { path: 'privacy-policy', element: <NavigateHelperComponent target={privacyLink} /> },
                { path: 'terms', element: <NavigateHelperComponent target={termsLink} /> },
                // Old redirected routes:
                { path: 'magicmirror/*', element: <Navigate to={pathWithParams('/')} /> },
                { path: 'magic-mirror/*', element: <Navigate to={pathWithParams('/')} /> },
                { path: 'pods/*', element: <Navigate to={pathWithParams('/')} /> },
                { path: 'dashboard/*', element: <Navigate to={pathWithParams('/')} /> },
            ],
        },

        // Not found
        { path: '404', element: <PageNotFound /> },
        { path: '*', element: <PageNotFound /> },
    ];

    return useSentryRoutes(routesConfig);
}
